import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  CheckCircle2, Edit3, Gift, Loader2, Plus, RotateCcw, Trash2, Undo2, X,
} from 'lucide-react';
import client, { createIdempotencyKey } from '../lib/api';
import { useToast } from '../hooks/use-toast';
import { freespinRewardText, isFreespinOnlyBonus } from '../lib/bonusReward';
import ConfirmDialog from './components/ConfirmDialog';
import { formatDateTime, formatMoney } from './lib/formatters';

const blankGrant = {
  mode: 'campaign', bonus_id: '', title: '', amount: 100,
  wager_multiplier: 0, note: '',
};

const statusLabel = {
  active: 'Aktif', completed: 'Tamamlandı', cancelled: 'İptal', expired: 'Süresi doldu',
  lost: 'Bakiye bitti',
};

// Liste iki kaynaktan gelir: kampanya/promosyon bonusu ve kayıp bonusu
// (cashback). İkisi de aynı bonus cüzdanını kilitler ve aynı uçlarla
// yönetilir; satırda yalnızca kaynağı ayırt eden bir rozet durur.
const sourceBadge = {
  cashback: { label: 'Kayıp Bonusu', className: 'badge-blue' },
};

const UserBonusControl = ({ user, onChanged }) => {
  const [claims, setClaims] = useState([]);
  const [bonuses, setBonuses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [grantOpen, setGrantOpen] = useState(false);
  const [grant, setGrant] = useState(blankGrant);
  const [claimAction, setClaimAction] = useState(null);
  const [claimEdit, setClaimEdit] = useState(null);
  const [clearConfirm, setClearConfirm] = useState(false);
  const { toast } = useToast();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: claimData }, { data: bonusData }] = await Promise.all([
        client.get(`/admin/users/${user.id}/bonus-claims`),
        client.get('/admin/bonuses'),
      ]);
      setClaims(claimData.items || []);
      setBonuses(bonusData.items || []);
    } catch (error) {
      toast({ title: 'Bonus bilgileri alınamadı', description: error.response?.data?.detail || 'Bir hata oluştu' });
    } finally { setLoading(false); }
  }, [toast, user.id]);

  useEffect(() => { load(); }, [load]);

  // Üyenin toplam kalan çevrimi: profil verisi bonus + kayıp bonusu
  // yükümlülüklerini birlikte özetler; liste de artık iki kaynağı gösterir.
  const wagerRemaining = Number(user.wager_remaining || 0);

  const clearWager = async () => {
    setBusy(true);
    try {
      const { data } = await client.post(`/admin/users/${user.id}/wager/clear`);
      toast({
        title: data.cleared_count ? 'Kalan çevrim sıfırlandı' : 'Sıfırlanacak çevrim yok',
        description: data.cleared_count
          ? `${data.cleared_count} kayıt kapatıldı`
            + (data.released > 0 ? ` · ${formatMoney(data.released)} ana bakiyeye geçti.` : '.')
          : undefined,
      });
      await load();
      onChanged?.();
    } catch (error) {
      toast({
        title: 'Sıfırlanamadı',
        description: error.response?.data?.detail || 'İşlem başarısız',
      });
    } finally {
      setBusy(false);
      setClearConfirm(false);
    }
  };

  const selectedCampaign = useMemo(
    () => bonuses.find((item) => item.id === grant.bonus_id),
    [bonuses, grant.bonus_id],
  );
  const freespinOnly = (
    grant.mode === 'campaign' && isFreespinOnlyBonus(selectedCampaign)
  );
  const multiplier = grant.mode === 'campaign'
    ? Number(selectedCampaign?.wager_multiplier || 0)
    : Number(grant.wager_multiplier || 0);
  const wagerRequired = freespinOnly
    ? 0
    : Number(grant.amount || 0) * multiplier;

  const openGrant = () => {
    const first = bonuses.find((item) => item.active) || bonuses[0];
    setGrant({
      ...blankGrant,
      bonus_id: first?.id || '',
      amount: isFreespinOnlyBonus(first) ? 0 : blankGrant.amount,
    });
    setGrantOpen(true);
  };

  const submitGrant = async () => {
    if (grant.mode === 'campaign' && !grant.bonus_id) {
      toast({ title: 'Bonus kampanyası seçin' });
      return;
    }
    if (grant.mode === 'custom' && !grant.title.trim()) {
      toast({ title: 'Özel bonus başlığını girin' });
      return;
    }
    if (
      !freespinOnly
      && (!Number.isFinite(Number(grant.amount)) || Number(grant.amount) <= 0)
    ) {
      toast({ title: 'Geçerli bir bonus tutarı girin' });
      return;
    }
    setBusy(true);
    try {
      const { data } = await client.post(
        `/admin/users/${user.id}/bonus`,
        {
          mode: grant.mode,
          bonus_id: grant.mode === 'campaign' ? grant.bonus_id : null,
          title: grant.mode === 'custom' ? grant.title.trim() : null,
          amount: freespinOnly ? 0 : Number(grant.amount),
          wager_multiplier: grant.mode === 'custom' ? Number(grant.wager_multiplier || 0) : 0,
          note: grant.note.trim(),
        },
        { headers: { 'Idempotency-Key': createIdempotencyKey() } },
      );
      toast({
        title: 'Bonus kullanıcıya tanımlandı',
        description: Number(data.freespin_count || 0) > 0
          ? `${data.freespin_count} freespin ${data.freespin_status === 'awaiting_provider'
            ? 'sağlayıcı kuyruğuna alındı.'
            : 'oyuncunun hesabına tanımlandı.'}`
          : Number(data.wager_required || 0) > 0
            ? `${formatMoney(data.claim?.amount)} bonus bakiyede kilitlendi.`
            : `${formatMoney(data.claim?.amount)} ana bakiyeye eklendi.`,
      });
      setGrantOpen(false);
      await load();
      onChanged?.();
    } catch (error) {
      toast({ title: 'Bonus verilemedi', description: error.response?.data?.detail || 'İşlem başarısız' });
    } finally { setBusy(false); }
  };

  const runClaimAction = async () => {
    if (!claimAction) return;
    const { kind, claim } = claimAction;
    setBusy(true);
    try {
      const { data } = kind === 'delete'
        ? await client.delete(`/admin/bonus-claims/${claim.id}`)
        : await client.patch(`/admin/bonus-claims/${claim.id}`, {
          status: kind === 'release' ? 'completed' : 'cancelled',
        });
      toast({
        title: kind === 'release' ? 'Çevrim tamamlandı sayıldı'
          : kind === 'cancel' ? 'Bonus iptal edildi' : 'Bonus kaydı silindi',
        description: Number(data.released || 0) > 0
          ? `${formatMoney(data.released)} ana bakiyeye aktarıldı.`
          : Number(data.revoked || 0) > 0
            ? `${formatMoney(data.revoked)} kilitli tutar geri alındı.`
            : 'Bakiye değişmedi.',
      });
      setClaimAction(null);
      await load();
      onChanged?.();
    } catch (error) {
      toast({ title: 'İşlem başarısız', description: error.response?.data?.detail || 'Bonus güncellenemedi' });
    } finally { setBusy(false); }
  };

  const saveClaimEdit = async () => {
    setBusy(true);
    try {
      const { data } = await client.patch(`/admin/bonus-claims/${claimEdit.id}`, {
        wager_required: Number(claimEdit.wager_required),
        wager_completed: Number(claimEdit.wager_completed),
      });
      toast({
        title: 'Çevrim güncellendi',
        description: Number(data.released || 0) > 0
          ? `${formatMoney(data.released)} ana bakiyeye aktarıldı.`
          : `Yeni şart ${formatMoney(data.claim.wager_required)}.`,
      });
      setClaimEdit(null);
      await load();
      onChanged?.();
    } catch (error) {
      toast({ title: 'Güncellenemedi', description: error.response?.data?.detail || 'İşlem başarısız' });
    } finally { setBusy(false); }
  };

  const confirmCopy = () => {
    if (!claimAction) return {};
    const { kind, claim } = claimAction;
    const locked = claim.funds_locked && claim.status === 'active';
    if (kind === 'release') return {
      title: 'Çevrimi tamamlandı say',
      description: `${claim.bonus_title} çevrimi affedilecek.${locked ? ` Kilitli ${formatMoney(claim.amount)} ana bakiyeye geçer.` : ''}`,
      confirmLabel: 'Çevrimi affet', tone: 'warning',
    };
    if (kind === 'cancel') return {
      title: 'Bonusu iptal et',
      description: `${claim.bonus_title} iptal edilecek.${locked ? ` Kilitli ${formatMoney(claim.amount)} geri alınır.` : ' Bakiye değişmez.'}`,
      confirmLabel: 'Bonusu geri al', tone: 'danger',
    };
    return {
      title: 'Bonus kaydını sil',
      description: `${claim.bonus_title} kaydı silinecek.${locked ? ` Kilitli ${formatMoney(claim.amount)} önce geri alınır.` : ' Tutar kullanıcıda kalır.'}`,
      confirmLabel: 'Kaydı sil', tone: 'danger',
    };
  };

  return (
    <section id="bonus-control" className="admin-card overflow-hidden scroll-mt-24">
      <div className="flex flex-col gap-3 border-b border-white/5 p-5 sm:flex-row sm:items-center">
        <div className="flex items-center gap-3">
          <div className="grid h-10 w-10 place-items-center rounded-xl border border-violet-400/20 bg-violet-400/10 text-violet-300"><Gift size={18} /></div>
          <div><h2 className="font-bold">Bonus Kontrol</h2><p className="text-xs text-slate-500">Kullanıcıya özel bonuslar ve çevrim yönetimi</p></div>
        </div>
        <div className="flex flex-wrap gap-2 sm:ml-auto">
          {/* Kalan çevrim bonus + kayıp bonusu kayıtlarının toplamıdır ve
              ikisi de listede durur; düğme hepsini tek işlemde kapatır. */}
          {wagerRemaining > 0 && (
            <button
              onClick={() => setClearConfirm(true)}
              disabled={busy}
              className="admin-btn-secondary inline-flex items-center justify-center gap-2 text-amber-300"
              title="Bonus ve kayıp bonusu çevrimlerinin tamamını kapatır"
            >
              <RotateCcw size={14} /> Çevrimi Manuel Sıfırla ({formatMoney(wagerRemaining)})
            </button>
          )}
          <button onClick={openGrant} className="admin-btn-primary inline-flex items-center justify-center gap-2"><Plus size={14} /> Bonus Ver</button>
        </div>
      </div>

      {loading ? <div className="p-10 text-center"><Loader2 className="inline animate-spin text-cyan-400" /></div> : (
        <div className="overflow-x-auto">
          <table className="admin-table w-full min-w-[900px]">
            <thead><tr><th>BONUS</th><th>ÖDÜL</th><th>ÇEVRİM</th><th>KALAN</th><th>DURUM</th><th>TARİH</th><th>İŞLEM</th></tr></thead>
            <tbody>
              {claims.map((claim) => {
                const progress = Number(claim.progress_percent || 0);
                return (
                  <tr key={claim.id}>
                    <td>
                      <div className="flex flex-wrap items-center gap-1.5">
                        <span className="font-semibold text-white">{claim.bonus_title}</span>
                        {sourceBadge[claim.source] && (
                          <span className={`badge-pill ${sourceBadge[claim.source].className}`}>
                            {sourceBadge[claim.source].label}
                          </span>
                        )}
                      </div>
                      {/* Kampanya bonusunda kayıt kimliği, kayıp bonusunda
                          dönem anahtarı ("2026-09-21") ayırt edicidir. */}
                      <div className="text-[10px] text-slate-600">
                        {claim.source === 'cashback'
                          ? [claim.period_key, Number(claim.percent) > 0 ? `%${Number(claim.percent)}` : '']
                            .filter(Boolean).join(' · ')
                          : claim.bonus_id}
                      </div>
                    </td>
                    <td className="font-bold text-emerald-400">
                      {Number(claim.freespin_count || 0) > 0 && Number(claim.amount || 0) <= 0
                        ? `${claim.freespin_count} Freespin`
                        : `+${formatMoney(claim.amount)}`}
                    </td>
                    <td className="min-w-[155px]"><div className="font-semibold text-amber-300">{formatMoney(claim.wager_completed)} / {formatMoney(claim.wager_required)}</div><div className="mt-1.5 h-1.5 overflow-hidden rounded-full bg-white/10"><div className="h-full rounded-full bg-gradient-to-r from-amber-500 to-yellow-300" style={{ width: `${Math.min(100, progress)}%` }} /></div></td>
                    <td className={Number(claim.wager_remaining) > 0 ? 'font-bold text-amber-400' : 'text-emerald-400'}>{Number(claim.wager_remaining) > 0 ? formatMoney(claim.wager_remaining) : 'Tamamlandı'}</td>
                    <td><span className={`badge-pill ${claim.status === 'active' ? 'badge-yellow' : claim.status === 'completed' ? 'badge-green' : 'badge-red'}`}>{statusLabel[claim.status] || claim.status}</span></td>
                    <td className="text-xs text-slate-400">{formatDateTime(claim.created_at)}</td>
                    <td><div className="flex gap-1.5">{claim.status === 'active' && <><button title="Çevrimi affet" onClick={() => setClaimAction({ kind: 'release', claim })} className="icon-btn view"><CheckCircle2 size={13} /></button><button title="Çevrimi düzenle" onClick={() => setClaimEdit({ ...claim })} className="icon-btn view"><Edit3 size={13} /></button><button title="Bonusu iptal et" onClick={() => setClaimAction({ kind: 'cancel', claim })} className="icon-btn ban"><Undo2 size={13} /></button></>}<button title="Kaydı sil" onClick={() => setClaimAction({ kind: 'delete', claim })} className="icon-btn ban"><Trash2 size={13} /></button></div></td>
                  </tr>
                );
              })}
              {!claims.length && <tr><td colSpan={7} className="py-10 text-center text-slate-500">Bu kullanıcıya ait bonus kaydı yok.</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {grantOpen && createPortal((
        <div className="admin-portal-theme fixed inset-0 z-[110] flex items-start justify-center overflow-y-auto p-4 sm:items-center" onClick={() => !busy && setGrantOpen(false)}>
          <div className="absolute inset-0 bg-black/80 backdrop-blur-md" />
          <div className="relative admin-card my-auto w-full max-w-lg max-h-[calc(100dvh-2rem)] overflow-y-auto" onClick={(event) => event.stopPropagation()}>
            <div className="flex items-center gap-3 border-b border-white/5 p-5"><div><h3 className="font-bold">{user.username} kullanıcısına bonus ver</h3><p className="text-xs text-slate-500 mt-0.5">Kampanya seçin veya özel bonus tanımlayın.</p></div><button onClick={() => setGrantOpen(false)} className="icon-btn view ml-auto"><X size={14} /></button></div>
            <div className="space-y-4 p-5">
              <div className="grid grid-cols-2 rounded-xl border border-white/10 bg-black/20 p-1"><button onClick={() => setGrant({ ...grant, mode: 'campaign', amount: isFreespinOnlyBonus(selectedCampaign) ? 0 : (Number(grant.amount) > 0 ? grant.amount : blankGrant.amount) })} className={`rounded-lg px-3 py-2 text-sm font-semibold ${grant.mode === 'campaign' ? 'bg-cyan-400/15 text-cyan-300' : 'text-slate-500'}`}>Mevcut Kampanya</button><button onClick={() => setGrant({ ...grant, mode: 'custom', amount: Number(grant.amount) > 0 ? grant.amount : blankGrant.amount })} className={`rounded-lg px-3 py-2 text-sm font-semibold ${grant.mode === 'custom' ? 'bg-violet-400/15 text-violet-300' : 'text-slate-500'}`}>Özel Bonus</button></div>
              {grant.mode === 'campaign' ? <div><label className="mb-1 block text-xs text-slate-400">Bonus Kampanyası</label><select value={grant.bonus_id} onChange={(e) => { const campaign = bonuses.find((item) => item.id === e.target.value); setGrant({ ...grant, bonus_id: e.target.value, amount: isFreespinOnlyBonus(campaign) ? 0 : (Number(grant.amount) > 0 ? grant.amount : blankGrant.amount) }); }} className="admin-input"><option value="">Kampanya seçin</option>{bonuses.map((bonus) => <option key={bonus.id} value={bonus.id}>{bonus.title}{isFreespinOnlyBonus(bonus) ? ` · ${freespinRewardText(bonus)}` : ` · x${bonus.wager_multiplier}`}{bonus.active ? '' : ' (pasif)'}</option>)}</select></div> : <><div><label className="mb-1 block text-xs text-slate-400">Özel Bonus Başlığı</label><input value={grant.title} onChange={(e) => setGrant({ ...grant, title: e.target.value })} className="admin-input" placeholder="Örn. VIP Doğum Günü Bonusu" /></div><div><label className="mb-1 block text-xs text-slate-400">Çevrim Çarpanı (x)</label><input type="number" min="0" max="1000" step="1" value={grant.wager_multiplier} onChange={(e) => setGrant({ ...grant, wager_multiplier: e.target.value })} className="admin-input" /></div></>}
              {!freespinOnly && <div><label className="mb-1 block text-xs text-slate-400">Verilecek Tutar (₺)</label><input type="number" min="0.01" step="0.01" value={grant.amount} onChange={(e) => setGrant({ ...grant, amount: e.target.value })} className="admin-input" /></div>}
              {freespinOnly ? (
                <div className="rounded-xl border border-violet-400/20 bg-violet-400/5 p-3 text-xs text-violet-200">
                  {freespinRewardText(selectedCampaign)} oyuncunun sağlayıcı
                  hesabına tanımlanır; nakit veya bonus bakiyesi değişmez.
                </div>
              ) : (
                <div className={`rounded-xl border p-3 text-xs ${wagerRequired > 0 ? 'border-amber-400/20 bg-amber-400/5 text-amber-300' : 'border-emerald-400/20 bg-emerald-400/5 text-emerald-300'}`}>{wagerRequired > 0 ? `${formatMoney(grant.amount)} bonus bakiyeye kilitlenir; ${formatMoney(wagerRequired)} çevrim tamamlanınca ana bakiyeye geçer.` : `${formatMoney(grant.amount)} çevrimsiz olarak doğrudan ana bakiyeye eklenir ve çekilebilir.`}</div>
              )}
              <div><label className="mb-1 block text-xs text-slate-400">Yönetici Notu (opsiyonel)</label><textarea value={grant.note} onChange={(e) => setGrant({ ...grant, note: e.target.value })} className="admin-input min-h-20" /></div>
              <button disabled={busy} onClick={submitGrant} className="admin-btn-primary w-full py-3">{busy && <Loader2 size={14} className="mr-2 inline animate-spin" />}{freespinOnly ? 'Freespin Tanımla' : 'Bonusu Tanımla'}</button>
            </div>
          </div>
        </div>
      ), document.body)}

      {claimEdit && createPortal((
        <div className="admin-portal-theme fixed inset-0 z-[110] flex items-center justify-center overflow-y-auto p-4" onClick={() => !busy && setClaimEdit(null)}><div className="absolute inset-0 bg-black/80 backdrop-blur-md" /><div className="relative admin-card w-full max-w-md max-h-[calc(100dvh-2rem)] overflow-y-auto" onClick={(event) => event.stopPropagation()}><div className="flex items-center border-b border-white/5 p-5"><div><h3 className="font-bold">Çevrim Düzenle</h3><p className="text-xs text-slate-500">{claimEdit.bonus_title}</p></div><button onClick={() => setClaimEdit(null)} className="icon-btn view ml-auto"><X size={14} /></button></div><div className="space-y-3 p-5"><div><label className="mb-1 block text-xs text-slate-400">Toplam Çevrim (₺)</label><input type="number" min="0" step="0.01" value={claimEdit.wager_required} onChange={(e) => setClaimEdit({ ...claimEdit, wager_required: e.target.value })} className="admin-input" /></div><div><label className="mb-1 block text-xs text-slate-400">Tamamlanan Çevrim (₺)</label><input type="number" min="0" step="0.01" value={claimEdit.wager_completed} onChange={(e) => setClaimEdit({ ...claimEdit, wager_completed: e.target.value })} className="admin-input" /></div><button disabled={busy} onClick={saveClaimEdit} className="admin-btn-primary w-full py-3">Kaydet</button></div></div></div>
      ), document.body)}

      <ConfirmDialog
        open={clearConfirm}
        title="Kalan çevrimi manuel sıfırla"
        description={`${user.username} kullanıcısının kalan ${formatMoney(wagerRemaining)} çevrim şartı silinecek. Bonus ve kayıp bonusu kayıtlarının tamamı tamamlanmış sayılır; kilitli bonus tutarı ana bakiyeye geçer ve çekilebilir olur. Bu işlem geri alınamaz.`}
        confirmLabel="Çevrimi sıfırla"
        tone="warning"
        busy={busy}
        onCancel={() => !busy && setClearConfirm(false)}
        onConfirm={clearWager}
      />
      <ConfirmDialog open={Boolean(claimAction)} busy={busy} onCancel={() => !busy && setClaimAction(null)} onConfirm={runClaimAction} {...confirmCopy()} />
    </section>
  );
};

export default UserBonusControl;
